import { ReactElement } from 'react';
import { Authentication } from 'utils';
import { Navigate } from 'react-router-dom';
import {
  selectPermissionCodes,
  selectPermissionsError,
  selectPermissionsInitialized,
} from 'store/features/permissions/selector';
import { useAppSelector } from 'store/hooks';
import { hasAnyPermission } from 'utils/permissions';
import LoadingBackdrop from 'components/loading-backdrop';
import { PRIVATE_ROUTE_REGISTRY, PUBLIC_ROUTE_REGISTRY } from 'constants/routes';

type RouteGuardProps = {
  access: 'guest' | 'private';
  children: ReactElement;
  anyOfPermissions?: readonly string[];
};

const RouteGuard = ({ access, children, anyOfPermissions = [] }: RouteGuardProps) => {
  const permissionCodes = useAppSelector(selectPermissionCodes);
  const permissionsError = useAppSelector(selectPermissionsError);
  const permissionsInitialized = useAppSelector(selectPermissionsInitialized);
  const hasSession = Authentication.hasSession();

  if (access === 'guest') {
    return hasSession ? <Navigate to={PRIVATE_ROUTE_REGISTRY.dashboard.path} /> : children;
  }

  if (!hasSession) {
    return <Navigate to={PUBLIC_ROUTE_REGISTRY.login.path} />;
  }

  if (anyOfPermissions.length === 0) {
    return children;
  }

  if (!permissionsInitialized) {
    return permissionsError ? (
      <Navigate to={PRIVATE_ROUTE_REGISTRY.dashboard.path} replace />
    ) : (
      <LoadingBackdrop open />
    );
  }

  return hasAnyPermission(permissionCodes, anyOfPermissions) ? (
    children
  ) : (
    <Navigate to={PRIVATE_ROUTE_REGISTRY.dashboard.path} />
  );
};

export default RouteGuard;
